import * as THREE from 'three';
import { ITEMS, RARITY_COLORS } from 'shared';
import type { ItemDefinition } from 'shared';

export interface LootDropConfig {
  scene: THREE.Scene;
  position: THREE.Vector3;
  itemId: string;
  quantity: number;
}

export class LootDrop {
  public mesh: THREE.Group;
  public id: string;
  public itemId: string;
  public quantity: number;
  public item: ItemDefinition;

  private floatOffset: number = 0;
  private baseY: number;
  private spawnTime: number;

  // Pickup
  public pickupRange: number = 2;

  constructor(config: LootDropConfig) {
    this.id = `loot_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    this.itemId = config.itemId;
    this.quantity = config.quantity;
    this.item = ITEMS[config.itemId];
    this.spawnTime = Date.now();

    this.mesh = new THREE.Group();

    // Create glowing orb for loot
    const rarityColor = RARITY_COLORS[this.item?.rarity || 'common'];
    const color = new THREE.Color(rarityColor);

    // Outer glow sphere
    const glowGeometry = new THREE.SphereGeometry(0.3, 16, 12);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.3,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    this.mesh.add(glow);

    // Inner core
    const coreGeometry = new THREE.SphereGeometry(0.15, 12, 8);
    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    this.mesh.add(core);

    // Item icon (floating text/emoji would be complex, using colored box)
    const iconGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
    const iconMaterial = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.5,
    });
    const icon = new THREE.Mesh(iconGeometry, iconMaterial);
    icon.position.y = 0.4;
    this.mesh.add(icon);

    // Point light for glow effect
    const light = new THREE.PointLight(color, 0.5, 3);
    light.position.y = 0.2;
    this.mesh.add(light);

    // Set position
    this.mesh.position.copy(config.position);
    this.mesh.position.y = 0.5;
    this.baseY = this.mesh.position.y;

    // Random rotation offset
    this.floatOffset = Math.random() * Math.PI * 2;

    // Add to scene
    config.scene.add(this.mesh);
  }

  public update(deltaTime: number): void {
    // Floating animation
    this.floatOffset += deltaTime * 2;
    this.mesh.position.y = this.baseY + Math.sin(this.floatOffset) * 0.1;

    // Rotate
    this.mesh.rotation.y += deltaTime * 1.5;

    // Pulse scale based on time
    const pulse = 1 + Math.sin(this.floatOffset * 2) * 0.1;
    this.mesh.scale.setScalar(pulse);
  }

  public isInRange(playerPosition: THREE.Vector3): boolean {
    const distance = this.mesh.position.distanceTo(playerPosition);
    return distance <= this.pickupRange;
  }

  public getAge(): number {
    return Date.now() - this.spawnTime;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public destroy(): void {
    this.mesh.parent?.remove(this.mesh);
  }
}
